class ProductCardGallery extends HTMLElement {
  connectedCallback() {
    this.carousel = this.querySelector('scroll-carousel');
    this.slides = Array.from(this.querySelectorAll('.product-card-gallery__slide'));
    this.card = this.closest('product-card, .product-card-variant, .product-card--fc');

    if (!this.carousel || this.slides.length < 2) return;

    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    let dragStartX = null;
    let dragStartScrollLeft = 0;
    let suppressClick = false;

    const startDrag = (clientX) => {
      dragStartX = clientX;
      dragStartScrollLeft = this.carousel.scrollLeft;
      suppressClick = false;
    };

    const trackDrag = (clientX) => {
      if (dragStartX === null) return;

      if (
        Math.abs(clientX - dragStartX) > 8 ||
        Math.abs(this.carousel.scrollLeft - dragStartScrollLeft) > 8
      ) {
        suppressClick = true;
      }
    };

    this.carousel.addEventListener('pointerdown', (event) => startDrag(event.clientX), { signal });
    this.carousel.addEventListener('pointermove', (event) => trackDrag(event.clientX), { signal });
    this.carousel.addEventListener('mousedown', (event) => startDrag(event.clientX), { signal });
    this.carousel.addEventListener('mousemove', (event) => trackDrag(event.clientX), { signal });
    this.carousel.addEventListener('touchstart', (event) => startDrag(event.touches[0]?.clientX), {
      passive: true,
      signal,
    });
    this.carousel.addEventListener('touchmove', (event) => trackDrag(event.touches[0]?.clientX), {
      passive: true,
      signal,
    });

    this.carousel.addEventListener('dragstart', (event) => {
      suppressClick = true;
      event.preventDefault();
    }, { signal });

    this.carousel.addEventListener('click', (event) => {
      const hadPointerGesture = dragStartX !== null;
      const didScroll = hadPointerGesture && Math.abs(this.carousel.scrollLeft - dragStartScrollLeft) > 8;
      dragStartX = null;

      if (!suppressClick && !didScroll) return;

      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
    }, { capture: true, signal });

    this.carousel.addEventListener('carousel:change', (event) => {
      this.updateActiveSlide(event.detail.index);
    }, { signal });

    this.carousel.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        document.dir === 'rtl' ? this.carousel.next() : this.carousel.previous();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        document.dir === 'rtl' ? this.carousel.previous() : this.carousel.next();
      }
    }, { signal });

    this.card?.addEventListener('change', (event) => {
      if (event.target.matches('[type="radio"]')) this.carousel.select(0, { instant: true });
    }, { signal });

    this.card?.addEventListener('click', (event) => {
      if (event.target.closest('.swatch-color')) this.carousel.select(0, { instant: true });
    }, { signal });

    this.updateActiveSlide(0);
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  setVariantImages(images, productUrl) {
    if (!this.carousel || !Array.isArray(images) || images.length === 0) return;

    const hiddenIndexes = [];

    this.slides.forEach((slide, index) => {
      const image = slide.querySelector('img');
      const imageData = images[index];

      if (!imageData || !image) {
        hiddenIndexes.push(index);
        return;
      }

      if (imageData.srcset) {
        image.srcset = imageData.srcset;
      } else {
        image.removeAttribute('srcset');
      }
      image.src = imageData.src;
      image.alt = imageData.alt || '';

      if (imageData.width) image.width = imageData.width;
      if (imageData.height) image.height = imageData.height;
      if (index > 0) image.loading = 'lazy';

      if (productUrl) slide.href = productUrl;

      if (imageData.label) slide.setAttribute('aria-label', imageData.label);
    });

    this.carousel.filter(hiddenIndexes);
    this.carousel.select(0, { instant: true });
    this.updateActiveSlide(0);
  }

  updateActiveSlide(activeIndex) {
    this.slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      slide.tabIndex = isActive ? 0 : -1;
    });
  }
}

if (!customElements.get('product-card-gallery')) {
  customElements.define('product-card-gallery', ProductCardGallery);
}
